/**
 * Hours report — who worked where, doing what, for how long.
 *
 * The end-of-day / end-of-week sheet Granules keeps by hand today: every
 * worker with the lines they worked, the job they did, the days and the
 * hours, overtime called out, with a line view for the same numbers the
 * other way round. Pick a range (today, this week, last week or any two
 * dates), search a name or a line, download CSV, or print — the print
 * stylesheet prints just the table.
 */
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, CircularProgress, InputAdornment, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { addDays, endOfWeek, format, startOfDay, startOfWeek } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { productionPlanApi } from "../../../services/production-plan-api";
import rosterAssignmentsApi, { RosterAssignment } from "../../../services/roster-assignments-api";
import { planner,PlannerHeader,PlannerPage,SectionLabel } from "../planner-ui"; // eslint-disable-line sort-imports
import { ManufacturingOrder } from "../production-plan/utils/calculations";
import { toLocalIso } from "../utils/time";
import { buildHoursReport, fmtHours, HoursReport, reportCsv } from "./utils/hours-report"; // eslint-disable-line sort-imports

type Preset = "today" | "week" | "lastWeek" | "custom";
type Grouping = "worker" | "line";

interface StoreLocation {
	id: number;
	name: string;
}

interface StoreWorkcenter {
	id: number;
	name: string;
	locationId?: number;
}

const toggleSx = {
	bgcolor: planner.surface,
	border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
	borderRadius: `${planner.radiusSm}px`,
	p: 0.25,
	gap: 0.25,
	"& .MuiToggleButton-root": {
		...planner.type.bodyStrong,
		fontFamily: planner.fontUi,
		border: 0,
		borderRadius: `${planner.radiusXs}px`,
		px: 1.5,
		py: 0.4,
		textTransform: "none",
		color: planner.inkSecondary,
		"&.Mui-selected": { bgcolor: planner.accentSoft, color: planner.accent, "&:hover": { bgcolor: planner.accentSoft } },
	},
};

const cellSx = { py: 0.75, px: 1.25, borderBottom: `1px solid ${planner.hairline}`, ...planner.type.body, color: planner.ink, verticalAlign: "top" as const };
const numSx = { ...cellSx, fontFamily: planner.fontMono, fontVariantNumeric: "tabular-nums" as const, textAlign: "right" as const, whiteSpace: "nowrap" as const };
const headSx = { ...cellSx, ...planner.labelCaps, color: planner.inkMuted, bgcolor: planner.surfaceSubtle, borderBottom: `1px solid ${planner.hairlineStrong}` };

const rangeFor = (preset: Preset, custom: { from: string; to: string }): { from: Date; to: Date } => {
	const today = startOfDay(new Date());
	if (preset === "today") return { from: today, to: addDays(today, 1) };
	if (preset === "week") {
		const monday = startOfWeek(today, { weekStartsOn: 1 });
		return { from: monday, to: addDays(endOfWeek(today, { weekStartsOn: 1 }), 0).getTime() ? addDays(monday, 7) : addDays(monday, 7) };
	}
	if (preset === "lastWeek") {
		const monday = addDays(startOfWeek(today, { weekStartsOn: 1 }), -7);
		return { from: monday, to: addDays(monday, 7) };
	}
	const parse = (s: string): Date => {
		const [y, m, d] = s.split("-").map(Number);
		return new Date(y, m - 1, d);
	};
	const from = custom.from ? parse(custom.from) : today;
	const to = custom.to ? addDays(parse(custom.to), 1) : addDays(from, 1);
	return { from, to: to > from ? to : addDays(from, 1) };
};

const HoursReportPage: React.FC = () => {
	const selectedLocation = useSelector((state: { fullTimeShifts?: { selectedLocation?: number } }) => Number(state.fullTimeShifts?.selectedLocation || 0));
	const locations = useSelector((state: { fullTimeShifts?: { filterData?: { allLocations?: StoreLocation[] } } }) => state.fullTimeShifts?.filterData?.allLocations ?? []);
	const allWorkcenters = useSelector((state: { fullTimeShifts?: { filterData?: { allWorkcenters?: StoreWorkcenter[] } } }) => state.fullTimeShifts?.filterData?.allWorkcenters ?? []);
	const locationName = locations.find((l) => l.id === selectedLocation)?.name ?? "";
	// The plant's lines, by name — orders in the range do not carry a location, so this is what scopes them.
	const plantLineNames = useMemo(
		() => new Set(allWorkcenters.filter((wc) => !selectedLocation || wc.locationId === selectedLocation).map((wc) => String(wc.name).trim().toLowerCase())),
		[allWorkcenters, selectedLocation]
	);

	const [preset, setPreset] = useState<Preset>("week");
	const [custom, setCustom] = useState({ from: format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"), to: format(new Date(), "yyyy-MM-dd") });
	const [grouping, setGrouping] = useState<Grouping>("worker");
	const [query, setQuery] = useState("");
	const [assignments, setAssignments] = useState<RosterAssignment[]>([]);
	const [orders, setOrders] = useState<ManufacturingOrder[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const range = useMemo(() => rangeFor(preset, custom), [preset, custom]);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		const fromIso = toLocalIso(range.from);
		const toIso = toLocalIso(range.to);
		Promise.all([rosterAssignmentsApi.getByDateRange(fromIso, toIso), productionPlanApi.getByDateRange(fromIso, toIso)])
			.then(([rows, plan]) => {
				if (cancelled) return;
				setAssignments(rows);
				setOrders(plan);
			})
			.catch((err) => {
				console.error("Hours report load failed:", err);
				if (!cancelled) setError("Could not load the roster for that range.");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [range]);

	// Scope to the plant: orders on its lines, and blocks on those orders or on those lines.
	const report: HoursReport = useMemo(() => {
		const onPlant = (name?: string | null): boolean => plantLineNames.size === 0 || (Boolean(name) && plantLineNames.has(String(name).trim().toLowerCase()));
		const plantOrders = orders.filter((o) => onPlant(o.workstation));
		const plantOrderIds = new Set(plantOrders.map((o) => o.id));
		const scoped = assignments.filter((a) => plantOrderIds.has(a.workOrderId) || onPlant(a.workstation));
		return buildHoursReport(scoped, plantOrders, range.from, range.to);
	}, [assignments, orders, plantLineNames, range]);

	const q = query.trim().toLowerCase();
	const workers = useMemo(
		() => (q ? report.workers.filter((w) => w.workerName.toLowerCase().includes(q) || w.lines.some((l) => l.workstation.toLowerCase().includes(q) || l.jobRole.toLowerCase().includes(q))) : report.workers),
		[report.workers, q]
	);
	const lines = useMemo(
		() => (q ? report.lines.filter((l) => l.workstation.toLowerCase().includes(q) || l.workers.some((w) => w.workerName.toLowerCase().includes(q) || w.jobRole.toLowerCase().includes(q))) : report.lines),
		[report.lines, q]
	);

	const rangeLabel = `${format(range.from, "EEE d MMM")} – ${format(addDays(range.to, -1), "EEE d MMM yyyy")}`;
	const handlePreset = useCallback((_e: React.MouseEvent<HTMLElement>, value: Preset | null) => value && setPreset(value), []);
	const handleGrouping = useCallback((_e: React.MouseEvent<HTMLElement>, value: Grouping | null) => value && setGrouping(value), []);
	const handleQuery = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value), []);
	const handleFrom = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setCustom((c) => ({ ...c, from: e.target.value }));
		setPreset("custom");
	}, []);
	const handleTo = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setCustom((c) => ({ ...c, to: e.target.value }));
		setPreset("custom");
	}, []);
	const handleCsv = useCallback(() => {
		const blob = new Blob([reportCsv(report)], { type: "text/csv;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `hours-${locationName || "plant"}-${format(range.from, "yyyyMMdd")}-${format(addDays(range.to, -1), "yyyyMMdd")}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	}, [report, locationName, range]);
	const handlePrint = useCallback(() => window.print(), []);

	return (
		<PlannerPage>
			<PlannerHeader
				title="Hours"
				subtitle={`${locationName ? `${locationName} · ` : ""}${rangeLabel}`}
				actions={
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
						<ToggleButtonGroup value={preset} exclusive size="small" onChange={handlePreset} sx={toggleSx} aria-label="range">
							<ToggleButton value="today">Today</ToggleButton>
							<ToggleButton value="week">This week</ToggleButton>
							<ToggleButton value="lastWeek">Last week</ToggleButton>
						</ToggleButtonGroup>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
							<input type="date" value={custom.from} onChange={handleFrom} aria-label="From" style={{ font: "inherit", fontFamily: planner.fontMono, fontSize: 13, padding: "5px 8px", border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`, borderRadius: planner.radiusSm, background: planner.surface }} />
							<span style={{ color: planner.inkMuted }}>–</span>
							<input type="date" value={custom.to} onChange={handleTo} aria-label="To" style={{ font: "inherit", fontFamily: planner.fontMono, fontSize: 13, padding: "5px 8px", border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`, borderRadius: planner.radiusSm, background: planner.surface }} />
						</Box>
						<Button size="small" startIcon={<DownloadIcon />} onClick={handleCsv} disabled={!report.workers.length} sx={{ textTransform: "none", color: planner.ink }}>
							CSV
						</Button>
						<Button size="small" startIcon={<PrintIcon />} onClick={handlePrint} disabled={!report.workers.length} sx={{ textTransform: "none", color: planner.ink }}>
							Print
						</Button>
					</Box>
				}
			/>

			<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
				<ToggleButtonGroup value={grouping} exclusive size="small" onChange={handleGrouping} sx={toggleSx} aria-label="group by">
					<ToggleButton value="worker">By worker</ToggleButton>
					<ToggleButton value="line">By line</ToggleButton>
				</ToggleButtonGroup>
				<TextField
					size="small"
					value={query}
					onChange={handleQuery}
					placeholder="Find a person or a line"
					InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: planner.inkMuted }} /></InputAdornment> }}
					sx={{ width: 240, "& .MuiInputBase-root": { ...planner.type.body, bgcolor: planner.surface } }}
				/>
				<Box sx={{ display: "flex", gap: 3, ml: "auto" }}>
					{[
						["People", String(report.totalPeople)],
						["Hours", fmtHours(report.totalHours)],
						["Overtime", fmtHours(report.totalOvertimeHours)],
					].map(([label, value]) => (
						<Box key={label} sx={{ textAlign: "right" }}>
							<SectionLabel>{label}</SectionLabel>
							<Typography sx={{ ...planner.type.stat, color: label === "Overtime" && report.totalOvertimeHours > 0 ? planner.partial : planner.ink }}>{value}</Typography>
						</Box>
					))}
				</Box>
			</Box>

			{/* print-target: the app's print stylesheet prints this and nothing else */}
			<Box className="print-target" sx={{ bgcolor: planner.surface, border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`, borderRadius: `${planner.radius}px`, overflow: "hidden" }}>
				<Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${planner.hairline}`, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
					<Typography sx={{ ...planner.type.title, color: planner.navy }}>
						Hours {grouping === "worker" ? "by worker" : "by line"}{locationName ? ` · ${locationName}` : ""}
					</Typography>
					<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontMono, color: planner.inkMuted }}>
						{rangeLabel} · {report.totalPeople} people · {fmtHours(report.totalHours)}{report.totalOvertimeHours > 0 ? ` · ${fmtHours(report.totalOvertimeHours)} overtime` : ""}
					</Typography>
				</Box>
				{loading && (
					<Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
						<CircularProgress size={22} sx={{ color: planner.accent }} />
					</Box>
				)}
				{!loading && error && <Typography sx={{ p: 2, ...planner.type.body, color: planner.unstaffed }}>{error}</Typography>}
				{!loading && !error && report.workers.length === 0 && (
					<Typography sx={{ p: 3, ...planner.type.body, color: planner.inkMuted }}>No roster blocks in this range.</Typography>
				)}
				{!loading && !error && report.workers.length > 0 && grouping === "worker" && (
					<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
						<thead>
							<tr>
								<Box component="th" sx={{ ...headSx, textAlign: "left" }}>Worker</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "left" }}>Work center</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "left" }}>Job</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "left" }}>Products / batches</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "right" }}>Days</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "right" }}>Hours</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "right" }}>Overtime</Box>
							</tr>
						</thead>
						<tbody>
							{workers.map((w) => (
								<React.Fragment key={w.workerId}>
									{w.lines.map((l, index) => (
										<tr key={`${w.workerId}-${l.workstation}-${l.jobRole}`} className="no-break">
											<Box component="td" sx={{ ...cellSx, fontWeight: 600, color: planner.navy, borderBottom: index === w.lines.length - 1 ? `1px solid ${planner.hairline}` : "none" }}>
												{index === 0 ? w.workerName : ""}
											</Box>
											<Box component="td" sx={cellSx}>{l.workstation}</Box>
											<Box component="td" sx={{ ...cellSx, color: planner.inkSecondary }}>{l.jobRole}</Box>
											<Box component="td" sx={{ ...cellSx, color: planner.inkSecondary }}>{l.products.join(", ")}</Box>
											<Box component="td" sx={numSx}>{l.days.length}</Box>
											<Box component="td" sx={numSx}>{fmtHours(l.hours)}</Box>
											<Box component="td" sx={{ ...numSx, color: l.overtimeHours > 0 ? planner.partial : planner.inkMuted }}>{l.overtimeHours > 0 ? fmtHours(l.overtimeHours) : "—"}</Box>
										</tr>
									))}
									{w.lines.length > 1 && (
										<tr>
											<Box component="td" colSpan={4} sx={{ ...cellSx, color: planner.inkMuted, fontSize: 12, textAlign: "right", bgcolor: planner.surfaceSubtle }}>{w.workerName} total</Box>
											<Box component="td" sx={{ ...numSx, bgcolor: planner.surfaceSubtle }}>{w.days}</Box>
											<Box component="td" sx={{ ...numSx, fontWeight: 700, bgcolor: planner.surfaceSubtle }}>{fmtHours(w.hours)}</Box>
											<Box component="td" sx={{ ...numSx, bgcolor: planner.surfaceSubtle, color: w.overtimeHours > 0 ? planner.partial : planner.inkMuted }}>{w.overtimeHours > 0 ? fmtHours(w.overtimeHours) : "—"}</Box>
										</tr>
									)}
								</React.Fragment>
							))}
						</tbody>
					</Box>
				)}
				{!loading && !error && report.workers.length > 0 && grouping === "line" && (
					<Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
						<thead>
							<tr>
								<Box component="th" sx={{ ...headSx, textAlign: "left" }}>Work center</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "left" }}>Worker</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "left" }}>Job</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "right" }}>Days</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "right" }}>Hours</Box>
								<Box component="th" sx={{ ...headSx, textAlign: "right" }}>Overtime</Box>
							</tr>
						</thead>
						<tbody>
							{lines.map((line) => (
								<React.Fragment key={line.workstation}>
									{line.workers.map((w, index) => (
										<tr key={`${line.workstation}-${w.workerId}-${w.jobRole}`} className="no-break">
											<Box component="td" sx={{ ...cellSx, fontWeight: 600, color: planner.navy, borderBottom: "none" }}>{index === 0 ? line.workstation : ""}</Box>
											<Box component="td" sx={cellSx}>{w.workerName}</Box>
											<Box component="td" sx={{ ...cellSx, color: planner.inkSecondary }}>{w.jobRole}</Box>
											<Box component="td" sx={numSx}>{w.days.length}</Box>
											<Box component="td" sx={numSx}>{fmtHours(w.hours)}</Box>
											<Box component="td" sx={{ ...numSx, color: w.overtimeHours > 0 ? planner.partial : planner.inkMuted }}>{w.overtimeHours > 0 ? fmtHours(w.overtimeHours) : "—"}</Box>
										</tr>
									))}
									<tr>
										<Box component="td" colSpan={3} sx={{ ...cellSx, color: planner.inkMuted, fontSize: 12, textAlign: "right", bgcolor: planner.surfaceSubtle }}>{line.workstation} · {line.people} people</Box>
										<Box component="td" sx={{ ...numSx, bgcolor: planner.surfaceSubtle }} />
										<Box component="td" sx={{ ...numSx, fontWeight: 700, bgcolor: planner.surfaceSubtle }}>{fmtHours(line.hours)}</Box>
										<Box component="td" sx={{ ...numSx, bgcolor: planner.surfaceSubtle, color: line.overtimeHours > 0 ? planner.partial : planner.inkMuted }}>{line.overtimeHours > 0 ? fmtHours(line.overtimeHours) : "—"}</Box>
									</tr>
								</React.Fragment>
							))}
						</tbody>
					</Box>
				)}
			</Box>
			{report.unmatchedOrders > 0 && (
				<Typography sx={{ mt: 1, ...planner.type.meta, color: planner.inkMuted }}>
					{report.unmatchedOrders} block{report.unmatchedOrders === 1 ? "" : "s"} belong to orders outside this range&apos;s plan; they are listed by line without a product.
				</Typography>
			)}
		</PlannerPage>
	);
};

export default HoursReportPage;
